import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

function Home() {
  const { theme, cycleTheme } = useTheme();

  return (
    <div className="container mx-auto my-8 px-4">
      <h2
        className={`text-5xl md:text-6xl font-light mb-6 ${theme.text} font-handwriting`}
      >
        Herzlich willkommen – Schön, dass Du hier bist!
      </h2>

      <div className="grid md:grid-cols-5 gap-8 items-center">
        <div className="md:col-span-3 order-2 md:order-1">
          <p className="text-lg leading-relaxed mb-6 text-slate-700">
            Jeder Mensch begegnet im Laufe seines Lebens Herausforderungen. Die
            Frage ist: Wie gehen wir damit um?
          </p>

          <div
            className={`${theme.box} p-8 rounded-lg shadow-lg mb-8 text-center`}
          >
            <blockquote className="text-2xl italic font-handwriting text-slate-800 mb-4">
              "Ob du glaubst etwas erreichen zu können oder nicht - du liegst
              immer richtig."
            </blockquote>
            <p className="text-slate-600">- Henry Ford</p>
          </div>

          <div className="text-lg leading-relaxed mb-6 text-slate-700">
            <h3 className="text-xl font-semibold mb-4 text-slate-800">
              Wir haben zwei Möglichkeiten:
            </h3>
            <div>
              • Wir können die Herausforderung als Chance sehen, neue
              Perspektiven einzunehmen, unsere eigenen Ressourcen zu nutzen und
              aktiv unser Leben zu gestalten.
            </div>
            <br />
            <div>
              • Oder wir ziehen uns zurück und verharren in alten Mustern, ohne
              Veränderung zuzulassen.
            </div>
          </div>

          <p className="text-lg leading-relaxed mb-8 text-slate-700">
            Wenn Sie sich bewusst für Entwicklung, Selbstbestimmung und neue
            Wege entscheiden und sich dabei Unterstützung wünschen, dann
            begleite ich Sie gerne auf Ihrem Weg.
          </p>
          <Link
            to="/contact"
            className={`inline-block ${theme.button} text-white px-8 py-3 rounded-lg transition-colors shadow-md`}
          >
            Kontakt aufnehmen
          </Link>
        </div>
        <div className="md:col-span-2 order-1 md:order-2 mb-6 md:mb-0">
          <img
            src="/images/lisaDog.jpeg"
            alt="Lisa Fuchsen mit Hund"
            className="w-full max-w-lg mx-auto rounded-lg shadow-lg cursor-pointer"
            onClick={cycleTheme}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-12 my-16">
        <div className={`${theme.box} p-8 rounded-lg shadow-lg`}>
          <h3 className="text-2xl font-semibold mb-6 text-slate-800">
            Meine Schwerpunkte
          </h3>
          <ul className="space-y-3 text-lg text-slate-700">
            <li>• Persönlichkeitsentwicklung & Selbstfindung</li>
            <li>• Selbstbewusstsein und Selbstwertgefühl stärken</li>
            <li>• Eigene Muster erkennen, hinterfragen und reflektieren</li>
            <li>• Beziehungs- und Bindungsthemen klären</li>
            <li>• Aktuelle Krisen bewältigen</li>
            <li>• Wendepunkte und Veränderungen meistern</li>
            <li>• Neuorientierung im privaten oder beruflichen Kontext</li>
            <li>• Erziehungsfragen reflektieren</li>
          </ul>
        </div>

        <div
          className={`bg-gradient-to-br ${theme.gradient} p-8 rounded-lg shadow-lg text-white`}
        >
          <h3 className="text-2xl font-bold mb-6">Meine Beratungsangebote</h3>

          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-bold mb-3">
                Walk & Talk – Beratung in Bewegung
              </h4>
              <p className="text-lg">
                Meine Beratung findet draußen in der Natur statt. Während
                unseres gemeinsamen Spaziergangs nutzen wir die Bewegung und
                Natur als Reflexionsraum für Ihre Themen.
              </p>
            </div>

            <div>
              <h4 className="text-xl font-bold mb-3">Online-Beratung</h4>
              <p className="text-lg">
                Alternativ biete ich professionelle Beratung per Videochat an.
                Dies ermöglicht Ihnen eine ortsunabhängige Beratung von zu Hause
                aus.
              </p>
            </div>
          </div>

          <Link
            to="/services"
            className="inline-block bg-white text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-100 transition-colors shadow-md mt-6"
          >
            Mehr erfahren
          </Link>
        </div>
      </div>

      <div
        className={`bg-gradient-to-br ${theme.gradient} rounded-lg shadow-lg p-8 text-white`}
      >
        <div className="text-center">
          <h3 className="text-3xl font-bold mb-4">
            Vereinbaren Sie ein Erstgespräch
          </h3>
          <p className="text-lg mb-8 text-blue-100">
            In einem unverbindlichen und kostenlosen Erstgespräch lernen wir uns
            kennen und besprechen Ihre Situation.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="tel:+491762029374"
              className="inline-block bg-white text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-100 transition-colors shadow-md"
            >
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                Anrufen
              </div>
            </a>
            <a
              href="mailto:stepintochange.withlisa@gmail.com"
              className="inline-block bg-white text-slate-800 px-6 py-3 rounded-lg hover:bg-slate-100 transition-colors shadow-md"
            >
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                E-Mail schreiben
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
